import { useState, useEffect, useCallback } from 'react';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';
import { agentsAPI, skillsAPI, casosPasadosAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Loader2, Save, AlertTriangle, PenLine, ShieldAlert, Power, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Qué puede hacer este agente — en acciones, no en documentos.
 *
 * **La pregunta que la pantalla de skills no contestaba.** Un catálogo de markdown con URLs y
 * formularios de credenciales no dice si el agente puede registrar un caso, leer el código o
 * escribirle a otro agente, que es lo único que trae quien viene a configurarlo.
 *
 * **Acá no hay ni una URL, ni un método, ni una credencial**, y no es prolijidad: es el límite
 * que separa esta pantalla del catálogo. La acción se ve; con qué se cumple se decide en
 * Conexiones, y con qué credencial, en la conexión.
 *
 * **Lo que se muestra lo calcula el backend con las mismas funciones que usa el turno.** Una
 * segunda lista en el cliente sería una pantalla que dice una cosa y un turno que hace otra.
 */
export default function QuePuedeHacer({ tenantId, capsuleId, agente, botonAI, onGuardado }) {
  const [datos, setDatos] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  const [sucio, setSucio] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [abierta, setAbierta] = useState(null);

  const agentId = agente?.agent_id;

  // El alcance se guarda **al elegirlo**, no con un boton aparte. Es una decision de una sola
  // perilla y dejarla sucia hasta que alguien apriete Guardar es la forma de que quede a medio
  // aplicar: la pantalla diria una cosa y el turno haria otra.
  const elegirAlcance = async (plano, valor) => {
    if (plano !== 'casos') return;
    try {
      await casosPasadosAPI.alcance(tenantId, capsuleId, agentId, valor);
      toast.success(
        valor === 'del_area' ? 'Ahora puede ver casos de cualquier solicitante'
        : valor === 'propio' ? 'Ahora ve solo los casos de quien le escribe'
        : 'Deja de usar casos anteriores'
      );
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo cambiar el alcance'));
    }
  };

  const cargar = useCallback(async () => {
    if (!tenantId || !capsuleId || !agentId) return;
    setCargando(true);
    try {
      const { data } = await agentsAPI.quePuedeHacer(tenantId, capsuleId, agentId);
      setDatos(data);
      setAsignaciones(data.asignaciones || []);
      setSucio(false);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo leer qué puede hacer este agente');
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId, agentId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Se escribe de vuelta el mismo documento con un campo cambiado: reconstruirlo desde la vista
  // perdería lo que la vista no muestra (la prioridad, por ejemplo) sin que nadie se entere.
  const cambiar = (skillId, cambio) => {
    setAsignaciones((prev) => {
      const existe = prev.find((s) => s.skill_id === skillId);
      if (existe) return prev.map((s) => (s.skill_id === skillId ? { ...s, ...cambio } : s));
      return [...prev, { skill_id: skillId, enabled: true, priority: prev.length + 1, overrides: {}, ...cambio }];
    });
    setSucio(true);
  };

  const prender = (skillId, activa) => cambiar(skillId, { enabled: !activa });

  const escribirOverride = (skillId, texto) => {
    const actual = asignaciones.find((s) => s.skill_id === skillId);
    cambiar(skillId, { overrides: { ...(actual?.overrides || {}), extra_instructions: texto } });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await skillsAPI.updateAgentSkills(tenantId, capsuleId, agentId, asignaciones);
      toast.success('Listo: esto es lo que puede hacer el agente');
      await cargar();
      onGuardado?.(asignaciones);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  if (!agentId) return null;
  if (cargando && !datos) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 py-8 justify-center">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando qué puede hacer…
      </div>
    );
  }
  if (!datos) return null;

  const overrideDe = (skillId) =>
    asignaciones.find((s) => s.skill_id === skillId)?.overrides?.extra_instructions || '';
  const activaAhora = (skillId) =>
    !!asignaciones.find((s) => s.skill_id === skillId)?.enabled;

  return (
    <div className="space-y-4" data-testid="que-puede-hacer">
      <QueSeConfiguraAca
        testId="ayuda-que-puede-hacer"
        queEs="Lo que este agente puede hacer hoy, en acciones — no en documentos ni en nombres técnicos."
        paraQue="Antes se configuraba asignando «skills», y esa palabra significaba cuatro cosas distintas: prosa, un interruptor del producto, herramientas de un sistema externo, y credenciales. Acá se ve lo único que importa: qué puede hacer."
        aQueAfecta={<>A <b>este agente</b> y a nadie más. Lo calcula el mismo código que arma el turno, así que <b>es lo que el modelo va a ver</b> — no una aproximación.</>}
        ejemplo={<>Prender «leer una imagen» hace que las capturas que le manden se le inyecten como texto. Apagarlo hace que reciba un aviso de que no puede — y lo diga, en vez de inventar qué decía la imagen.</>}
        ojo={<><b>La cápsula manda por encima de esto.</b> Si el área no deja pasar una habilidad (<i>Config Cápsula → qué habilidades puede usar</i>), acá no aparece por más que la prendas. Permitir en la cápsula tampoco asigna: son dos pasos.</>}
      />

      <p className="text-[10px] text-zinc-500 max-w-3xl">
        Lo que este agente <b>puede hacer hoy</b>, en acciones. Sin URLs, sin métodos y sin
        credenciales: eso se configura en Conexiones, y acá sólo importa si el agente lo tiene o
        no. Lo calcula el mismo código que arma el turno, así que es lo que el modelo va a ver.
      </p>
      <p className="text-[10px] text-zinc-500 max-w-3xl">
        Que una acción esté acá quiere decir que <b>el agente la recibe y la puede pedir</b>. Si
        además funciona —la credencial, la red, el sistema del otro lado— lo contesta el ensayo,
        al final de la página: es la única forma de saberlo sin adivinar.
      </p>

      {sucio && (
        <div
          data-testid="cambios-sin-guardar"
          className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 flex items-center gap-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Hay cambios sin guardar. Las acciones de arriba siguen mostrando lo que el agente puede
          hacer <b>ahora</b>, no lo que va a poder: se actualizan al guardar.
        </div>
      )}

      {(datos.grupos || []).map((g, i) => (
        <Grupo key={`${g.plano}-${i}`} grupo={g} onElegir={(v) => elegirAlcance(g.plano, v)} />
      ))}

      <Card className="rounded-sm border-brand-border" data-testid="habilidades">
        <CardHeader className="py-2 px-3 border-b border-brand-border">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-700">
            Habilidades — el criterio con el que actúa
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <p className="text-[10px] text-zinc-500 px-3 py-2 border-b border-brand-border/60">
            Una habilidad es <b>prosa</b>: cuándo actuar y con qué cuidado. Algunas además traen
            acciones o piden una capacidad, y ésas son las que prenden los grupos de arriba.
          </p>
          {(datos.habilidades || []).map((h) => {
            const activa = activaAhora(h.skill_id);
            const abiertaEsta = abierta === h.skill_id;
            return (
              <div key={h.skill_id} className="border-b border-brand-border/60 last:border-b-0">
                <div className="flex items-start gap-3 px-3 py-2">
                  <Switch
                    data-testid={`habilidad-${h.skill_id}`}
                    checked={activa}
                    onCheckedChange={() => prender(h.skill_id, activa)}
                    className="mt-0.5"
                  />
                  <button
                    onClick={() => setAbierta(abiertaEsta ? null : h.skill_id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {abiertaEsta
                        ? <ChevronDown className="w-3 h-3 text-zinc-400" />
                        : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                      <span className={`text-xs font-medium ${activa ? 'text-[#09090B]' : 'text-zinc-400'}`}>
                        {h.nombre}
                      </span>
                      {h.activa_un_plano && (
                        <Badge variant="outline" className="text-[9px] rounded-sm px-1 py-0 h-4 border-brand-primary/40 text-brand-primary">
                          prende un grupo de arriba
                        </Badge>
                      )}
                      {h.solo_prosa && !h.activa_un_plano ? (
                        <Badge variant="secondary" className="text-[9px] rounded-sm px-1 py-0 h-4">sólo criterio</Badge>
                      ) : (
                        <>
                          {h.acciones > 0 && (
                            <Badge variant="secondary" className="text-[9px] rounded-sm px-1 py-0 h-4">
                              {h.acciones} {h.acciones === 1 ? 'acción' : 'acciones'}
                            </Badge>
                          )}
                          {h.capacidades > 0 && (
                            <Badge variant="secondary" className="text-[9px] rounded-sm px-1 py-0 h-4">
                              {h.capacidades} {h.capacidades === 1 ? 'capacidad' : 'capacidades'}
                            </Badge>
                          )}
                        </>
                      )}
                      {overrideDe(h.skill_id) && (
                        <Badge variant="outline" className="text-[9px] rounded-sm px-1 py-0 h-4 border-purple-300 text-purple-600">
                          con instrucción propia
                        </Badge>
                      )}
                    </div>
                    {h.descripcion && (
                      <p className="text-[10px] text-zinc-500 mt-0.5 pl-4">{h.descripcion}</p>
                    )}
                  </button>
                </div>

                {abiertaEsta && (
                  <div className="px-3 pb-3 pl-11">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider">
                        Instrucción sólo para {agente?.name || agentId}
                      </span>
                      {botonAI?.({
                        skillName: h.nombre,
                        onGenerated: (texto) => escribirOverride(h.skill_id, texto),
                      })}
                    </div>
                    <textarea
                      data-testid={`override-${h.skill_id}`}
                      value={overrideDe(h.skill_id)}
                      onChange={(e) => escribirOverride(h.skill_id, e.target.value)}
                      placeholder="Se suma a la prosa de la habilidad, sólo para este agente. Vacío = la habilidad tal cual."
                      className="w-full border border-purple-200 rounded-sm p-2 text-[10px] h-20 focus:ring-1 focus:ring-purple-400 focus:outline-none resize-none bg-purple-50/30 font-mono"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Button
        data-testid="guardar-que-puede-hacer"
        onClick={guardar}
        disabled={guardando || !sucio}
        className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8"
      >
        {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar
      </Button>
    </div>
  );
}

/** Un plano: lo que el agente puede hacer ahí, y qué interruptor lo prende. */
function Grupo({ grupo, onElegir }) {
  const { activo, acciones = [], interruptor, condicion, avisos = [], eleccion } = grupo;
  return (
    <Card
      className={`rounded-sm ${activo ? 'border-brand-border' : 'border-brand-border/50 bg-zinc-50/40'}`}
      data-testid={`plano-${grupo.plano}`}
    >
      <CardHeader className="py-2 px-3 border-b border-brand-border/60">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className={`text-xs font-semibold ${activo ? 'text-[#09090B]' : 'text-zinc-400'}`}>
              {grupo.titulo}
            </CardTitle>
            {grupo.que_es && <p className="text-[10px] text-zinc-500 mt-0.5 max-w-2xl">{grupo.que_es}</p>}
          </div>
          <Badge
            variant={activo ? 'default' : 'secondary'}
            className="text-[9px] rounded-sm px-1.5 py-0 h-4 shrink-0"
          >
            {activo ? 'puede' : 'no puede'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Un plano que se **elige** en vez de prenderse. Radio y no dos casillas: con dos
            independientes, "las dos marcadas" no significa nada --la amplia ya incluye a la
            acotada-- y la pantalla admitiria un estado que el sistema no tiene. Un radio
            codifica la verdad; dos checkboxes codifican una mentira posible. */}
        {eleccion ? (
          <div className="px-3 py-2 border-b border-brand-border/40 space-y-1.5"
               data-testid={`eleccion-${grupo.plano}`}>
            {(eleccion.opciones || []).map((o) => (
              <label key={o.valor} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="mt-0.5"
                  name={`eleccion-${grupo.plano}`}
                  value={o.valor}
                  checked={eleccion.valor === o.valor}
                  onChange={() => onElegir && onElegir(o.valor)}
                  data-testid={`opcion-${grupo.plano}-${o.valor}`}
                />
                <span>
                  <span className="text-[11px] text-zinc-800">{o.titulo}</span>
                  {o.que_hace && <span className="block text-[10px] text-zinc-500">{o.que_hace}</span>}
                  {o.ojo && (
                    <span className="block text-[10px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 shrink-0" /> {o.ojo}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        ) : interruptor ? (
          <p className="text-[10px] text-zinc-500 px-3 py-1.5 border-b border-brand-border/40 flex items-center gap-1.5">
            <Power className="w-3 h-3 shrink-0" />
            Lo prende {interruptor.texto}, acá abajo.
          </p>
        ) : (
          <p className="text-[10px] text-zinc-400 px-3 py-1.5 border-b border-brand-border/40 flex items-center gap-1.5">
            <Power className="w-3 h-3 shrink-0" />
            Lo trae el producto: no hay nada que prender.
          </p>
        )}

        {condicion && (
          <p
            data-testid={`condicion-${grupo.plano}`}
            className="text-[10px] text-zinc-600 bg-blue-50/60 px-3 py-1.5 border-b border-blue-200/60"
          >
            {condicion}
          </p>
        )}

        {avisos.map((aviso, i) => (
          <p
            key={i}
            data-testid={`aviso-${grupo.plano}`}
            className="text-[10px] text-amber-800 bg-amber-50 px-3 py-1.5 border-b border-amber-200 flex items-start gap-1.5"
          >
            <AlertTriangle className="w-3 h-3 shrink-0 mt-px" /> {aviso}
          </p>
        ))}

        <div className="divide-y divide-brand-border/40">
          {acciones.map((a) => (
            <div key={a.nombre} className="flex items-start gap-2 px-3 py-1.5">
              <span
                className={`text-[11px] leading-snug flex-1 ${
                  a.activa ? 'text-[#09090B]' : a.motivo ? 'text-amber-800' : 'text-zinc-400'
                }`}
              >
                {a.que_hace || a.nombre}
                {a.motivo && <span className="text-amber-700"> — {a.motivo}</span>}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {a.escribe && (
                  <span
                    title="Deja el sistema distinto"
                    className="text-[9px] text-zinc-500 border border-brand-border rounded-sm px-1 flex items-center gap-0.5"
                  >
                    <PenLine className="w-2.5 h-2.5" /> escribe
                  </span>
                )}
                {a.pide_autorizacion && (
                  <span
                    title={`Por ${a.por}${a.autoriza ? ` · autoriza ${a.autoriza}` : ''}`}
                    className="text-[9px] text-amber-700 border border-amber-300 bg-amber-50 rounded-sm px-1 flex items-center gap-0.5"
                  >
                    <ShieldAlert className="w-2.5 h-2.5" /> pide permiso
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
